import {
  Avatar,
  Box,
  Center,
  HStack,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { djs } from '@duke-hq/djs';
import { trpcAdmin } from '../../lib/trpcAdmin';
import { testIds } from '../testIds';
import { PlacementStatusBadge } from '../components/PlacementStatusBadge';

function formatDate(date: Date | null): string {
  return date != null ? djs(date).format('DD MMM YYYY') : '—';
}

export function EmployersPlacementsTab({ employerId }: { employerId: string }): React.ReactElement {
  const {
    data: placements,
    isLoading,
    isError,
  } = trpcAdmin.workPlacements.employers.listEmployerPlacements.useQuery({ employerId });

  if (isLoading) {
    return (
      <Center p={8} bg="bg.subtle" minH="100%">
        <Spinner size="xl" />
      </Center>
    );
  }

  if (isError || placements == null) {
    return (
      <Center p={8} bg="bg.subtle" minH="100%">
        <Text color="fg.muted" data-testid={testIds.placementsError}>
          <Trans>Unable to load placements. Close and try again.</Trans>
        </Text>
      </Center>
    );
  }

  if (placements.length === 0) {
    return (
      <Center p={8} bg="bg.subtle" minH="100%">
        <Text color="fg.muted" data-testid={testIds.placementsEmpty}>
          <Trans>No placements yet</Trans>
        </Text>
      </Center>
    );
  }

  return (
    <Box
      m={4}
      borderWidth="1px"
      borderColor="border.default"
      borderRadius="lg"
      overflowX="auto"
      bg="bg.subtle"
    >
      <Table data-testid={testIds.employerPlacementsTable}>
        <Thead>
          <Tr>
            <Th>
              <Trans>Student</Trans>
            </Th>
            <Th>
              <Trans>Stage</Trans>
            </Th>
            <Th>
              <Trans>Course</Trans>
            </Th>
            <Th>
              <Trans>Placement Start</Trans>
            </Th>
            <Th>
              <Trans>Placement End</Trans>
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {placements.map((placement) => (
            <Tr key={placement.id} data-testid={testIds.employerPlacementRow}>
              <Td>
                <HStack gap={3}>
                  <Avatar size="sm" name={placement.student.name} />
                  <Stack gap={0}>
                    <Text fontSize="sm" fontWeight="semibold">
                      {placement.student.name}
                    </Text>
                    {placement.student.email != null ? (
                      <Text fontSize="xs" color="fg.subtle">
                        {placement.student.email}
                      </Text>
                    ) : null}
                  </Stack>
                </HStack>
              </Td>
              <Td>
                <PlacementStatusBadge
                  status={placement.status}
                  onHold={placement.onHold}
                  data-testid={testIds.placementStatus}
                />
              </Td>
              <Td>
                <Text fontSize="sm">{placement.courseIdentifier ?? '—'}</Text>
              </Td>
              <Td>
                <Text fontSize="sm">{formatDate(placement.startedAt)}</Text>
              </Td>
              <Td>
                <Text fontSize="sm">{formatDate(placement.endedAt)}</Text>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
}
